/**
 * AccessDenied Component
 * Shown when user navigates to a route they don't have permission to access
 */

import { useNavigate } from 'react-router-dom';
import { ShieldX, ArrowLeft, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePermissions } from '@/hooks/usePermissions';

export const AccessDenied = ({ 
  title = "Access Denied",
  message = "You don't have permission to view this page.",
  showBackButton = true,
  showHomeButton = true,
}) => {
  const navigate = useNavigate();
  const { getDefaultPath, role } = usePermissions();
  
  const handleGoBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate(getDefaultPath());
    }
  };
  
  const handleGoHome = () => {
    navigate(getDefaultPath());
  };
  
  return (
    <div 
      className="min-h-[70vh] flex flex-col items-center justify-center px-4"
      data-testid="access-denied"
    >
      {/* Icon */}
      <div className="w-20 h-20 rounded-2xl bg-signal-red/10 flex items-center justify-center mb-8">
        <ShieldX className="w-10 h-10 text-signal-red" />
      </div>
      
      {/* Title */}
      <h1 className="text-3xl sm:text-4xl font-semibold tracking-[-0.02em] text-cloud-white mb-4 text-center">
        {title}
      </h1>
      
      {/* Message */}
      <p className="text-soft-silver text-base sm:text-lg text-center max-w-md mb-2">
        {message}
      </p>
      
      {/* Role hint */}
      {role && (
        <p className="text-muted-text text-sm mb-8">
          Your current role: <span className="text-soft-silver font-medium">{role.replace('_', ' ')}</span>
        </p>
      )}
      
      {/* Actions */}
      <div className="flex items-center gap-4">
        {showBackButton && (
          <Button
            variant="outline"
            onClick={handleGoBack}
            className="border-white/[0.12] text-soft-silver hover:text-cloud-white hover:bg-white/[0.05]"
            data-testid="access-denied-back"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        )}
        
        {showHomeButton && (
          <Button
            onClick={handleGoHome}
            className="bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
            data-testid="access-denied-home"
          >
            <Home className="w-4 h-4 mr-2" />
            Go to Dashboard
          </Button>
        )}
      </div>
    </div>
  );
};

export default AccessDenied;
